import axios from "axios";
import authHeader from "./auth-header";
import { logout } from "../reducers/actions";
import store from "../reducers/store";
import { refresh } from "../reducers/actions";

const axiosApiInstance = axios.create();
axiosApiInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response.status === 401 || error.response.status === 422) {

            //localStorage.removeItem("reduxState");
            logout()(store.dispatch);
        }

        return Promise.reject(error.message);
    }
);



class BackendService {

    isRequestCanceled = (error) => {
        return axios.isCancel(error) ||
            error?.name === 'AbortError' ||
            error?.name === 'CanceledError' ||
            error?.message === 'canceled' ||
            error?.code === 'ERR_CANCELED';
    };

    /** Uses only the caller-provided AbortSignal; no global request registry. */
    createRequestContext = (options = {}) => {
        return {
            signal: options.signal,
            cleanup: () => {}
        };
    };

    downloadRootCACert() {
        return axiosApiInstance
            .get(window.config.API_URL + "download-root-ca", { headers: authHeader() })
            .then(
                (response) => {
                    return response;
                },
                (error) => {
                    return [];
                }
            );
    }
    downloadAuthCACert() {
        return axiosApiInstance
            .get(window.config.API_URL + "download-auth-ca", { headers: authHeader() })
            .then(
                (response) => {
                    return response;
                },
                (error) => {
                    return [];
                }
            );
    }

    postDownfile = (endpoint, requestJson) => {
        let head = { headers: authHeader(), responseType: 'arraybuffer' };
        const requestContext = this.createRequestContext();
        if (requestContext.signal) {
            head.signal = requestContext.signal;
        }
        const makeRequest = async () => {
            try {
                const response = await axios.post(window.config.API_URL + endpoint, requestJson, head );
                return response.data
            }
            catch (error) {
                if (this.isRequestCanceled(error)) {
                    throw error;
                }

                if (error.toString().includes('Error: Network Error')) {
                    console.error('Call to ' + endpoint + ' failed: Connection to Red Access unavailable')
                    return null;
                }

                if (error.toString().includes('401') || error.toString().includes('422')) {

                    if (authHeader() == "") {
                        window.location = "/login";
                    }

                    await refresh();
                }
            }
        };
        return makeRequest().finally(requestContext.cleanup);
    }

    postJSON = (endpoint, requestJson, reload = false, withmsg = false, downloadfile = false, options = {}, is_file = false) => {
        let head = { headers: authHeader() };
        let retryCount = 0;
        let maxRetries = 3;
        let authRetried = false;
        const requestContext = this.createRequestContext(options);
    
        if (is_file) {
            head = { headers: authHeader(), 'Content-Type': 'multipart/form-data' };
        }
    
        if (requestContext.signal) {
            head.signal = requestContext.signal;
        }
        
        if(options.responseType){
            head.responseType = options.responseType;
        }

        if (typeof options.timeout === "number" && options.timeout > 0) {
            head.timeout = options.timeout;
        }

        const makeRequest = async () => {
            try {
                const response = await axios.post(
                    window.config.API_URL + endpoint, 
                    requestJson, 
                    head
                );
    
                if (reload) {
                    window.location = window.location;
                } else {
                    if (downloadfile) {
                        return response.data;
                    }
                    if (!withmsg) {
                        return response.data.data ?? [];
                    } else {
                        return { data: response.data.data ?? [], msg: response.data.msg ?? "" };
                    }
                }
            } catch (error) {
                if (this.isRequestCanceled(error)) {
                    throw error;
                }
    
                if (retryCount < maxRetries && error.toString().includes('Error: Network Error')) {
                    retryCount++;
                    return makeRequest();
                } else {
                    if (error.toString().includes('Error: Network Error')) {
                        console.error('Call to ' + endpoint + ' failed: Connection to Red Access unavailable');
                        return null;
                    }
    
                    if (error.toString().includes('401') || error.toString().includes('422')) {
                        if (authHeader() === "") {
                            window.location = "/login";
                            return [];
                        }

                        /* Retry auth once. Recursing on every 401 never terminates when the
                           refreshed token is still rejected, which hangs the tab. */
                        if (authRetried) {
                            return [];
                        }
                        authRetried = true;

                        await refresh();
                        head.headers = authHeader();

                        return makeRequest();
                    } else if (error.toString().includes('403')) {
                        store.dispatch({
                            type: "TRIGGER_READONLY_POPUP",
                            payload: true,
                        });
                        return { data: [], msg: "" };
                    }
    
                    if (error.response) {
                        if (error.response.data) {
                            if (!withmsg) {
                                return error.response.data.data ?? [];
                            } else {
                                return { 
                                    data: error.response.data.data ?? [], 
                                    msg: error.response.data.msg ?? "" 
                                };
                            }
                        }
                    }
                }
    
                return [];
            }
        };
    
        return makeRequest().finally(requestContext.cleanup);
    };

    getJSON = (endpoint, requestJson, reload = false, withmsg = false, downloadfile = false, options = {}, is_file = false) => {
        let config = { 
            headers: authHeader(),
            params: requestJson
        };
        let retryCount = 0;
        let maxRetries = 3;
        let authRetried = false;
        const requestContext = this.createRequestContext(options);

        if (is_file) {
            config.headers = authHeader();
        }

        if (requestContext.signal) {
            config.signal = requestContext.signal;
        }
        
        if (options.responseType) {
            config.responseType = options.responseType;
        }

        if (typeof options.timeout === "number" && options.timeout > 0) {
            config.timeout = options.timeout;
        }

        const makeRequest = async () => {
            try {
                const response = await axios.get(
                    window.config.API_URL + endpoint, 
                    config
                );

                if (reload) {
                    window.location = window.location;
                    return;
                } else {
                    if (downloadfile) {
                        return response.data;
                    }
                    if (!withmsg) {
                        return response.data.data ?? [];
                    } else {
                        return { data: response.data.data ?? [], msg: response.data.msg ?? "" };
                    }
                }
            } catch (error) {
                if (this.isRequestCanceled(error)) {
                    throw error;
                }

                if (retryCount < maxRetries && error.toString().includes('Error: Network Error')) {
                    retryCount++;
                    return makeRequest();
                } else {
                    if (error.toString().includes('Error: Network Error')) {
                        console.error('Call to ' + endpoint + ' failed: Connection to Red Access unavailable');
                        return null;
                    }

                    if (error.toString().includes('401') || error.toString().includes('422')) {
                        if (authHeader() === "") {
                            window.location = "/login";
                            return;
                        }

                        /* Retry auth once — see the matching guard in postJSON. */
                        if (authRetried) {
                            return [];
                        }
                        authRetried = true;

                        await refresh();
                        config.headers = authHeader();

                        return makeRequest();
                    } else if (error.toString().includes('403')) {
                        store.dispatch({
                            type: "TRIGGER_READONLY_POPUP",
                            payload: true,
                        });
                        return { data: [], msg: "" };
                    }

                    if (error.response) {
                        if (error.response.data) {
                            if (!withmsg) {
                                return error.response.data.data ?? [];
                            } else {
                                return { 
                                    data: error.response.data.data ?? [], 
                                    msg: error.response.data.msg ?? "" 
                                };
                            }
                        }
                    }
                }

                return [];
            }
        };

        return makeRequest().finally(requestContext.cleanup);
    };


    createAbortController = () => {
        return new AbortController();
    };
}

export default new BackendService();
